import { useCallback, useEffect, useRef, useState } from "react";

import { useMedplum } from "@medplum/react";

import * as Sentry from "@sentry/react";

import {
  dismissMissingServiceEncounter,
  loadMissingServices,
} from "@/billing/services/missing-services-table";
import {
  LoadMissingServicesResult,
  MissingServicesFilters,
  MissingServicesSortDirection,
  MissingServicesTableRow,
} from "@/billing/types/missing-services";

export interface UseMissingServicesTableDataResult {
  rows: MissingServicesTableRow[];
  isLoading: boolean;
  error: Error | undefined;
  /** Exact number of Encounters still needing Services across the practice, for sizing the pager. */
  totalRows: number;
  /** Dismisses the Encounter and reloads this page. Resolves false, without throwing, on failure. */
  dismissEncounter: (encounterId: string) => Promise<boolean>;
}

/**
 * Loads one page of Missing Services rows for a practice, oldest first unless
 * `sortDirection` is `"desc"`. Returns no rows and no error while `practiceId` is
 * undefined, so the caller can render before the route param resolves. Load failures go to Sentry;
 * the caller shows the message.
 */
export function useMissingServicesTableData(
  practiceId: string | undefined,
  page: number,
  refreshKey = 0,
  filters?: MissingServicesFilters,
  sortDirection?: MissingServicesSortDirection,
): UseMissingServicesTableDataResult {
  const medplum = useMedplum();
  const [rows, setRows] = useState<MissingServicesTableRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const latestRequestIdRef = useRef(0);

  /** Resolves false, without throwing, when the load itself failed. */
  const load = useCallback(async (): Promise<boolean> => {
    if (!practiceId) {
      latestRequestIdRef.current += 1;
      setRows([]);
      setTotalRows(0);
      setError(undefined);
      setIsLoading(false);
      return true;
    }

    const requestId = ++latestRequestIdRef.current;
    setIsLoading(true);
    setError(undefined);

    // Passing `filters`/`sortDirection` positionally even when undefined would still add them as
    // explicit trailing arguments, changing loadMissingServices's call shape for every existing
    // caller. Omit each entirely when unset, so callers that don't pass them see the same call as
    // before.
    let result: LoadMissingServicesResult;
    if (filters && sortDirection) {
      result = await loadMissingServices(medplum, practiceId, page, filters, sortDirection);
    } else if (filters) {
      result = await loadMissingServices(medplum, practiceId, page, filters);
    } else {
      result = await loadMissingServices(medplum, practiceId, page);
    }
    if (requestId !== latestRequestIdRef.current) {
      return true;
    }

    if (result.ok) {
      setRows(result.data);
      setTotalRows(result.totalRows);
    } else {
      const loadError = new Error(`Missing Services load failed: ${result.reason}`);
      setRows([]);
      setTotalRows(0);
      setError(loadError);
      Sentry.captureException(loadError, { extra: { details: result.details } });
    }
    setIsLoading(false);
    return result.ok;
  }, [medplum, page, practiceId, filters, sortDirection]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const dismissEncounter = useCallback(
    async (encounterId: string) => {
      const result = await dismissMissingServiceEncounter(medplum, encounterId);
      if (!result.ok) {
        Sentry.captureException(new Error(`Missing Services dismiss failed: ${result.reason}`), {
          extra: { details: result.details },
        });
        return false;
      }
      return load();
    },
    [medplum, load],
  );

  return { rows, isLoading, error, totalRows, dismissEncounter };
}
